import { ChevronLeft, ChevronRight, Play } from "lucide-react";
import { Drawer, DrawerContent, DrawerTrigger } from "../ui/drawer";
import { GlobalYouTubeShortEmbed } from "./GlobalYouTubeShortEmbed";
import { useEffect, useRef, useState } from "react";
import { GlobalAnimatedLayout } from "../animation/GlobalAnimatedLayout";

interface YouTubeShort {
  id: number;
  videoId: string;
  thumbnail: string;
}

const youtubeShorts: YouTubeShort[] = [
  {
    id: 1,
    videoId: "W1COrw6HWrk",
    thumbnail:
      "https://ik.imagekit.io/didigi/bangun-rumah-di-bekasi/bangun-rumah-di-bekasi_thumb-1-b.webp",
  },
  {
    id: 2,
    videoId: "MrgxwHeNOss",
    thumbnail:
      "https://ik.imagekit.io/didigi/bangun-rumah-di-bekasi/bangun-rumah-di-bekasi_thumb-2.webp",
  },
  {
    id: 3,
    videoId: "mmHCOFKDdNM",
    thumbnail:
      "https://ik.imagekit.io/didigi/bangun-rumah-di-bekasi/bangun-rumah-di-bekasi_thumb-3.webp",
  },
  {
    id: 4,
    videoId: "ufjtTeXrppg",
    thumbnail:
      "https://ik.imagekit.io/didigi/bangun-rumah-di-bekasi/bangun-rumah-di-bekasi_thumb-4.webp",
  },
  {
    id: 5,
    videoId: "mmHCOFKDdNM",
    thumbnail:
      "https://ik.imagekit.io/didigi/bangun-rumah-di-bekasi/bangun-rumah-di-bekasi_thumb-5.webp",
  },
];

export function GlobalYouTubeShortDrawer() {
  const [isScrolledLeft, setIsScrolledLeft] = useState(false);
  const [isScrolledEnd, setIsScrolledEnd] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setIsScrolledLeft(scrollLeft > 0);
      setIsScrolledEnd(scrollLeft + clientWidth >= scrollWidth - 1);
      const index = Math.round(scrollLeft / clientWidth);
      setCurrentIndex(index);
    }
  };

  useEffect(() => {
    if (scrollRef.current) {
      const { scrollWidth, clientWidth } = scrollRef.current;
      setTotalPages(Math.ceil(scrollWidth / clientWidth));
    }
  }, [youtubeShorts]);

  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -300, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 300, behavior: "smooth" });
    }
  };

  return (
    <div className={`w-full flex flex-col rounded-xl `}>
      <section className="relative">
        <GlobalAnimatedLayout
          direction="left"
          className={`w-full`}
          threshold={0.1}
        >
          {isScrolledLeft && (
            <button
              onClick={scrollLeft}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-30 p-1 rounded-full shadow-xl bg-black text-yellow-500"
            >
              <ChevronLeft size={20} />
            </button>
          )}
          {!isScrolledEnd && (
            <button
              onClick={scrollRight}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-30 p-1 rounded-full shadow-xl bg-black text-yellow-500"
            >
              <ChevronRight size={20} />
            </button>
          )}
          <section
            ref={scrollRef}
            className={`w-full flex flex-row pb-[5rem] overflow-x-scroll snap-x snap-mandatory no-scrollbar lg:grid-cols-5 rounded-lg `}
            style={{ scrollSnapType: "x mandatory" }}
            onScroll={handleScroll}
          >
            <div
              className={`h-fit snap-center rounded-lg py-[1.5rem] min-w-[16rem]  flex items-center transition-opacity duration-500 ${
                isScrolledLeft ? "opacity-0" : "opacity-100"
              }`}
            >
              <img
                className="image-item lg:h-[40rem]"
                src="https://ik.imagekit.io/didigi/bangun-rumah-di-bekasi/bangun-rumah-di-bekasi_intro_1.webp"
                alt="arsinesia Background"
              />
            </div>
            {youtubeShorts.map((video) => (
              <Drawer key={video.id}>
                <DrawerTrigger className="" asChild>
                  <div className="flex gap-[4rem]  ml-4 pr-4 snap-center">
                    <button className="focus:outline-none w-full relative">
                      <div className="aspect-[9/16] w-full min-w-[240px] max-w-[280px] mx-auto relative">
                        <img
                          src={video.thumbnail}
                          alt={`YouTube Short ${video.id}`}
                          className="w-full h-full object-cover rounded-xl shadow-md hover:opacity-90 transition"
                        />
                        {/* Overlay Play Icon */}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="bg-black/50 p-4 rounded-full">
                            <Play className="w-10 h-10 text-white" />
                          </div>
                        </div>
                      </div>
                    </button>
                  </div>
                </DrawerTrigger>

                <DrawerContent className="h-[80vh] max-h-[80vh] w-full flex justify-center">
                  <GlobalYouTubeShortEmbed videoId={video.videoId} />
                </DrawerContent>
              </Drawer>
            ))}
          </section>
        </GlobalAnimatedLayout>
      </section>
      <section className="z-40  lg:hidden flex justify-center -mt-[12rem] ">
        {Array.from({ length: totalPages }).map((_, index) => (
          <div
            key={index}
            className={`w-3 h-3 rounded-full mx-1 transition-all ${
              index === currentIndex
                ? "bg-yellow-500 px-[2rem] ml-[1rem] mr-[1rem] scale-125"
                : "bg-gray-400"
            }`}
          />
        ))}
      </section>
    </div>
  );
}
